import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api';
import {
  Upload, FileCheck, AlertTriangle, CheckCircle2, XCircle,
  Loader2, ChevronDown, ChevronRight, Package, ArrowRightLeft,
  Search, Shield, Clock, FileText, BarChart3, FileWarning, ExternalLink, Copy,
  MessageSquare,
} from 'lucide-react';

const STATUS_CONFIG = {
  extracting: { label: 'Extracting', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/20', border: 'border-blue-300 dark:border-blue-800/30' },
  matching: { label: 'Matching', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-300 dark:border-amber-800/30' },
  under_review: { label: 'Under Review', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-300 dark:border-amber-800/30' },
  complete: { label: 'Complete', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/20', border: 'border-emerald-300 dark:border-emerald-800/30' },
  error: { label: 'Error', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/20', border: 'border-red-300 dark:border-red-800/30' },
};

const MATCH_CONFIG = {
  exact_match: { label: 'Exact Match', color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/20', icon: CheckCircle2 },
  substitution: { label: 'Substitution', color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/20', icon: AlertTriangle },
  no_match: { label: 'No Match', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/20', icon: XCircle },
};

export default function SubmittalPanel({ projectId }) {
  const [submittals, setSubmittals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tradeName, setTradeName] = useState('');
  const [subName, setSubName] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [subView, setSubView] = useState('submittals'); // 'submittals' | 'insights'
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, sumRes] = await Promise.all([
        api.get(`/closeout/projects/${projectId}/submittals`),
        api.get(`/closeout/projects/${projectId}/submittal-summary`),
      ]);
      setSubmittals(sRes.data);
      setSummary(sumRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll for extracting submittals
  useEffect(() => {
    const extracting = submittals.some(s => s.status === 'extracting' || s.status === 'matching');
    if (!extracting) return;
    const poll = setInterval(fetchData, 5000);
    return () => clearInterval(poll);
  }, [submittals, fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('trade_name', tradeName);
      formData.append('sub_name', subName);
      await api.post(`/closeout/projects/${projectId}/submittals/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { trade_name: tradeName, sub_name: subName },
      });
      setShowUpload(false);
      setTradeName('');
      setSubName('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const sc = summary?.status_counts || {};

  return (
    <div className="space-y-4" data-testid="submittal-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-heading uppercase tracking-wider text-foreground">
            Submittal Reconciliation
          </h3>
          {summary && (
            <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
              <span>{summary.total_submittals} submittal{summary.total_submittals !== 1 ? 's' : ''} uploaded</span>
              <span className="text-emerald-500">{sc.approved || 0} approved</span>
              <span className="text-amber-500">{sc.substitution_pending || 0} substitutions pending</span>
              <span className="text-red-500">{sc.rejected || 0} rejected</span>
              <span className="text-muted-foreground">{sc.awaiting_submittal || 0} awaiting submittal</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setShowUpload(!showUpload)}
            data-testid="upload-submittal-btn"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors bg-blue-600 hover:bg-blue-500 text-white">
            <Upload className="w-3.5 h-3.5" />
            Upload Submittal
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-border pb-1">
        {[
          { id: 'submittals', label: 'Submittals', icon: FileCheck },
          { id: 'insights', label: 'Insights', icon: BarChart3 },
        ].map(t => (
          <button key={t.id} onClick={() => setSubView(t.id)}
            data-testid={`subtab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
              subView === t.id ? 'bg-blue-100 dark:bg-blue-900/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
            }`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {subView === 'insights' ? (
        <InsightsView projectId={projectId} />
      ) : (
        <>
          {/* Upload Form */}
          {showUpload && (
            <div className="p-4 bg-card border border-border rounded-sm space-y-3" data-testid="upload-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Trade / Division</label>
                  <input value={tradeName} onChange={(e) => setTradeName(e.target.value)}
                    placeholder="e.g., Plumbing, HVAC, Electrical"
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid="trade-name-input" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Subcontractor</label>
                  <input value={subName} onChange={(e) => setSubName(e.target.value)}
                    placeholder="e.g., ABC Plumbing Inc."
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid="sub-name-input" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input type="file" ref={fileRef} accept=".pdf,.xlsx,.xls"
                  onChange={handleUpload} className="hidden" data-testid="submittal-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="select-file-btn"
                  className="flex items-center gap-1.5 px-4 py-2 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded-sm transition-colors disabled:opacity-50">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                  {uploading ? 'Uploading...' : 'Select PDF / Excel'}
                </button>
                <button onClick={() => setShowUpload(false)}
                  className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
              </div>
            </div>
          )}

          {/* Submittal List */}
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : submittals.length === 0 ? (
            <div className="text-center py-12 bg-card border border-dashed border-border rounded-sm">
              <FileCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No submittals uploaded yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Upload submittal PDFs from subcontractors to compare against specified equipment
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {submittals.map(s => (
                <SubmittalCard key={s.id} submittal={s} projectId={projectId} onRefresh={fetchData} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SubmittalCard({ submittal, projectId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [generatingRfi, setGeneratingRfi] = useState(null);
  const [rfiLink, setRfiLink] = useState(null);
  const [rfiMessages, setRfiMessages] = useState({});
  const [showRfiThread, setShowRfiThread] = useState(null);
  const [rfiReplyText, setRfiReplyText] = useState('');
  const [rfiReplying, setRfiReplying] = useState(false);
  const st = STATUS_CONFIG[submittal.status] || STATUS_CONFIG.under_review;
  const matches = submittal.matches || [];
  const exactMatches = matches.filter(m => m.match_type === 'exact_match' && !m.action);
  const substitutions = matches.filter(m => m.match_type === 'substitution' && !m.action);
  const noMatches = matches.filter(m => m.match_type === 'no_match');

  const handleAction = async (matchIdx, action) => {
    try {
      await api.post(`/closeout/projects/${projectId}/submittals/${submittal.id}/action`, {
        match_idx: matchIdx, action, notes: '',
      });
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const handleGenerateRfi = async (matchIdx) => {
    setGeneratingRfi(matchIdx);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/rfis/generate`, {
        submittal_id: submittal.id,
        match_idx: matchIdx,
      });
      const baseUrl = window.location.origin;
      setRfiLink({ idx: matchIdx, rfiNumber: res.data.rfi_number, rfiId: res.data.id, url: `${baseUrl}/rfi/${res.data.id}` });
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Failed to generate RFI');
    } finally {
      setGeneratingRfi(null);
    }
  };

  // Fetch RFI messages for the currently visible RFI
  const fetchRfiMessages = useCallback(async (rfiId) => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/rfis/${rfiId}/messages`);
      setRfiMessages(prev => ({ ...prev, [rfiId]: res.data || [] }));
    } catch {}
  }, [projectId]);

  const handleRfiReply = async (rfiId) => {
    if (!rfiReplyText.trim()) return;
    setRfiReplying(true);
    try {
      await api.post(`/closeout/projects/${projectId}/rfis/${rfiId}/messages`, { text: rfiReplyText });
      setRfiReplyText('');
      fetchRfiMessages(rfiId);
    } catch {}
    setRfiReplying(false);
  };

  const handleApproveAll = async () => {
    setApprovingAll(true);
    try {
      await api.post(`/closeout/projects/${projectId}/submittals/${submittal.id}/approve-all`);
      onRefresh();
    } catch (err) { console.error(err); }
    finally { setApprovingAll(false); }
  };

  return (
    <div className={`border rounded-sm bg-card ${st.border}`} data-testid={`submittal-${submittal.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <FileCheck className={`w-4 h-4 ${st.color} shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {submittal.trade_name || 'Submittal'}{submittal.sub_name ? ` — ${submittal.sub_name}` : ''}
          </p>
          <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
            <span>{submittal.file_name}</span>
            {matches.length > 0 && (
              <>
                <span className="text-emerald-500">{matches.filter(m => m.match_type === 'exact_match').length} exact</span>
                <span className="text-amber-500">{substitutions.length} substitution{substitutions.length !== 1 ? 's' : ''}</span>
                {noMatches.length > 0 && <span className="text-red-500">{noMatches.length} unmatched</span>}
              </>
            )}
            {submittal.extracted_items?.length > 0 && (
              <span>{submittal.extracted_items.length} items extracted</span>
            )}
          </div>
        </div>

        <span className={`text-[8px] font-heading uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${st.bg} ${st.color} border ${st.border}`}>
          {st.label}
        </span>
      </div>

      {/* Expanded: Reconciliation View */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-3">
          {/* Processing states */}
          {(submittal.status === 'extracting' || submittal.status === 'matching') && (
            <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {submittal.status === 'extracting' ? 'AI is reading the submittal...' : 'Matching against equipment records...'}
            </div>
          )}

          {submittal.status === 'error' && (
            <p className="text-xs text-red-500">{submittal.error_message || 'Extraction failed'}</p>
          )}

          {/* Bulk actions */}
          {matches.length > 0 && submittal.status === 'under_review' && (
            <div className="flex items-center gap-2">
              {exactMatches.length > 0 && (
                <button onClick={handleApproveAll} disabled={approvingAll}
                  data-testid={`approve-all-${submittal.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm transition-colors disabled:opacity-50">
                  {approvingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  Approve All Matches ({exactMatches.length})
                </button>
              )}
            </div>
          )}

          {/* Match cards */}
          {matches.map((m, idx) => {
            const mc = MATCH_CONFIG[m.match_type] || MATCH_CONFIG.no_match;
            const MatchIcon = mc.icon;
            const isActioned = !!m.action;
            const actionLabel = m.action === 'approve' ? 'Approved' : m.action === 'flag_substitution' ? 'Flagged' : m.action === 'reject' ? 'Rejected' : null;

            return (
              <React.Fragment key={idx}>
              <div className={`p-3 rounded-sm border ${isActioned ? 'border-border/50 opacity-70' : mc.bg + ' border ' + (m.match_type === 'exact_match' ? 'border-emerald-300 dark:border-emerald-800/30' : m.match_type === 'substitution' ? 'border-amber-300 dark:border-amber-800/30' : 'border-red-300 dark:border-red-800/30')}`}
                data-testid={`match-${idx}`}>
                <div className="flex items-start gap-3">
                  <MatchIcon className={`w-4 h-4 mt-0.5 shrink-0 ${mc.color}`} />
                  <div className="flex-1 min-w-0">
                    {/* Side-by-side comparison */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Specified */}
                      <div>
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Specified</p>
                        {m.specified ? (
                          <>
                            <p className="text-xs font-medium text-foreground">
                              {m.specified.make} {m.specified.model}
                            </p>
                            <p className="text-[10px] text-muted-foreground">{m.specified.equipment_type}</p>
                            {m.specified.location && (
                              <p className="text-[10px] text-muted-foreground">Location: {m.specified.location}</p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-red-500 italic">No matching equipment found</p>
                        )}
                      </div>

                      {/* Submitted */}
                      <div>
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Submitted</p>
                        <p className="text-xs font-medium text-foreground">
                          {m.submitted.make} {m.submitted.model}
                        </p>
                        <p className="text-[10px] text-muted-foreground">{m.submitted.equipment_type}</p>
                        {m.submitted.page_ref && (
                          <p className="text-[10px] text-muted-foreground">Ref: {m.submitted.page_ref}</p>
                        )}
                      </div>
                    </div>

                    {/* Match type badge + actions */}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-[8px] font-heading uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${mc.bg} ${mc.color}`}>
                        {mc.label}
                      </span>

                      {isActioned ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${
                            m.action === 'approve' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' :
                            m.action === 'flag_substitution' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' :
                            'bg-red-100 dark:bg-red-900/30 text-red-600'
                          }`}>
                            {actionLabel} by {m.action_by}
                          </span>
                          {m.action === 'flag_substitution' && (
                            rfiLink && rfiLink.idx === idx ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-blue-600 dark:text-blue-400 font-mono">{rfiLink.rfiNumber}</span>
                                <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(rfiLink.url); }}
                                  data-testid={`copy-rfi-link-${idx}`}
                                  className="flex items-center gap-1 px-1.5 py-0.5 text-[8px] text-blue-600 border border-blue-300 dark:border-blue-800/30 rounded-sm hover:bg-blue-50 dark:hover:bg-blue-950/20">
                                  <Copy className="w-2.5 h-2.5" /> Copy Link
                                </button>
                                <button onClick={(e) => {
                                    e.stopPropagation();
                                    if (showRfiThread === rfiLink.rfiId) { setShowRfiThread(null); }
                                    else { setShowRfiThread(rfiLink.rfiId); fetchRfiMessages(rfiLink.rfiId); }
                                  }}
                                  data-testid={`rfi-messages-btn-${idx}`}
                                  className={`flex items-center gap-1 px-1.5 py-0.5 text-[8px] border rounded-sm transition-colors ${
                                    showRfiThread === rfiLink.rfiId
                                      ? 'text-blue-600 border-blue-400 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800'
                                      : 'text-muted-foreground border-border hover:bg-muted/20'
                                  }`}>
                                  <MessageSquare className="w-2.5 h-2.5" />
                                  {rfiMessages[rfiLink.rfiId]?.length > 0 ? rfiMessages[rfiLink.rfiId].length : ''} Thread
                                </button>
                              </div>
                            ) : (
                              <button onClick={(e) => { e.stopPropagation(); handleGenerateRfi(idx); }}
                                disabled={generatingRfi === idx}
                                data-testid={`generate-rfi-${idx}`}
                                className="flex items-center gap-1 px-2 py-0.5 text-[9px] text-blue-600 border border-blue-300 dark:border-blue-800/30 rounded-sm hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors disabled:opacity-50">
                                {generatingRfi === idx ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <FileWarning className="w-2.5 h-2.5" />}
                                Generate RFI
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-1 ml-auto">
                          <button onClick={(e) => { e.stopPropagation(); handleAction(idx, 'approve'); }}
                            data-testid={`approve-match-${idx}`}
                            className="px-2 py-1 text-[9px] text-emerald-600 border border-emerald-300 dark:border-emerald-800/30 rounded-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors">
                            Approve
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleAction(idx, 'flag_substitution'); }}
                            data-testid={`flag-match-${idx}`}
                            className="px-2 py-1 text-[9px] text-amber-600 border border-amber-300 dark:border-amber-800/30 rounded-sm hover:bg-amber-50 dark:hover:bg-amber-950/20 transition-colors">
                            Flag Substitution
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleAction(idx, 'reject'); }}
                            data-testid={`reject-match-${idx}`}
                            className="px-2 py-1 text-[9px] text-red-600 border border-red-300 dark:border-red-800/30 rounded-sm hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors">
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Inline RFI Thread */}
              {showRfiThread && rfiLink?.rfiId === showRfiThread && rfiLink?.idx === idx && (
                <div className="mt-2 border border-blue-500/20 rounded-sm bg-card overflow-hidden" data-testid={`rfi-thread-${idx}`}>
                  <div className="px-3 py-2 border-b border-border bg-blue-500/5 flex items-center gap-2">
                    <MessageSquare className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                    <span className="text-[10px] font-heading uppercase tracking-widest text-foreground">RFI Thread — {rfiLink.rfiNumber}</span>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto px-3 py-2 space-y-2">
                    {(rfiMessages[showRfiThread] || []).length === 0 ? (
                      <p className="text-[10px] text-muted-foreground text-center py-4">No messages yet. Architect can start the conversation from the RFI link.</p>
                    ) : (
                      (rfiMessages[showRfiThread] || []).map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender_role === 'pm' ? 'justify-end' : 'justify-start'}`}>
                          <div className="max-w-[75%]">
                            <span className="text-[9px] text-muted-foreground">
                              {msg.sender_name || msg.sender_email} ({msg.sender_role === 'pm' ? 'You' : 'Architect'})
                            </span>
                            <div className={`rounded-sm px-2.5 py-1.5 text-[11px] ${
                              msg.sender_role === 'pm' ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-secondary border border-border'
                            } text-foreground`}>
                              {msg.text}
                            </div>
                            <span className="text-[8px] text-muted-foreground/60">{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
                    <input value={rfiReplyText} onChange={e => setRfiReplyText(e.target.value)}
                      placeholder="Reply to architect..." data-testid={`rfi-reply-input-${idx}`}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleRfiReply(showRfiThread); } }}
                      className="flex-1 px-2.5 py-1.5 text-[11px] bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
                    <button onClick={() => handleRfiReply(showRfiThread)} disabled={!rfiReplyText.trim() || rfiReplying}
                      data-testid={`rfi-reply-send-${idx}`}
                      className="px-2.5 py-1.5 text-[9px] font-heading uppercase bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-40">
                      {rfiReplying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reply'}
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
            );
          })}

          {matches.length === 0 && submittal.status === 'under_review' && (
            <p className="text-xs text-muted-foreground italic">No items extracted from this submittal.</p>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Insights Dashboard ──────────────────────────────────────────────────

function InsightsView({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/closeout/projects/${projectId}/submittal-insights`);
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  if (loading) return (
    <div className="flex items-center justify-center h-32">
      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data || data.trades.length === 0) return (
    <div className="text-center py-12 bg-card border border-dashed border-border rounded-sm">
      <BarChart3 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
      <p className="text-sm text-muted-foreground">No submittal data to analyze</p>
      <p className="text-xs text-muted-foreground mt-1">Upload submittals to see approval rates and trade insights</p>
    </div>
  );

  const { trades, totals } = data;

  return (
    <div className="space-y-5" data-testid="submittal-insights">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-sm p-3">
          <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Total Items</p>
          <p className="font-mono text-xl font-bold text-foreground mt-1">{totals.total_items}</p>
          <p className="text-[10px] text-muted-foreground">{totals.total_submittals} submittals</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-3">
          <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Approval Rate</p>
          <p className={`font-mono text-xl font-bold mt-1 ${totals.approval_rate >= 70 ? 'text-emerald-600 dark:text-emerald-400' : totals.approval_rate >= 40 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
            {totals.approval_rate}%
          </p>
          <p className="text-[10px] text-emerald-500">{totals.total_approved} approved</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-3">
          <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Substitution Rate</p>
          <p className={`font-mono text-xl font-bold mt-1 ${totals.substitution_rate <= 10 ? 'text-emerald-600 dark:text-emerald-400' : totals.substitution_rate <= 30 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
            {totals.substitution_rate}%
          </p>
          <p className="text-[10px] text-amber-500">{totals.total_substitutions} substitutions</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-3">
          <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Pending Review</p>
          <p className={`font-mono text-xl font-bold mt-1 ${totals.total_pending === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'}`}>
            {totals.total_pending}
          </p>
          <p className="text-[10px] text-muted-foreground">items awaiting action</p>
        </div>
      </div>

      {/* Trade Breakdown Table */}
      <div>
        <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mb-2">By Trade</p>
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-3 py-2 text-[9px] font-heading uppercase tracking-wider text-muted-foreground">Trade</th>
                <th className="text-left px-3 py-2 text-[9px] font-heading uppercase tracking-wider text-muted-foreground">Sub</th>
                <th className="text-center px-2 py-2 text-[9px] font-heading uppercase tracking-wider text-muted-foreground">Items</th>
                <th className="text-center px-2 py-2 text-[9px] font-heading uppercase tracking-wider text-emerald-500">Match</th>
                <th className="text-center px-2 py-2 text-[9px] font-heading uppercase tracking-wider text-amber-500">Subs</th>
                <th className="text-center px-2 py-2 text-[9px] font-heading uppercase tracking-wider text-emerald-500">Approved</th>
                <th className="text-center px-2 py-2 text-[9px] font-heading uppercase tracking-wider text-amber-500">Flagged</th>
                <th className="text-center px-2 py-2 text-[9px] font-heading uppercase tracking-wider text-red-500">Rejected</th>
                <th className="text-center px-2 py-2 text-[9px] font-heading uppercase tracking-wider text-blue-500">Pending</th>
                <th className="text-right px-3 py-2 text-[9px] font-heading uppercase tracking-wider text-muted-foreground">Approval</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => {
                const approvalRate = t.total_items > 0 ? Math.round(t.approved / t.total_items * 100) : 0;
                return (
                  <tr key={i} className="border-b border-border/50 hover:bg-muted/20 transition-colors" data-testid={`trade-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-foreground">{t.trade}</td>
                    <td className="px-3 py-2 text-muted-foreground truncate max-w-[120px]">{t.sub_name || '—'}</td>
                    <td className="text-center px-2 py-2 font-mono text-muted-foreground">{t.total_items}</td>
                    <td className="text-center px-2 py-2 font-mono text-emerald-600 dark:text-emerald-400">{t.exact_matches}</td>
                    <td className="text-center px-2 py-2 font-mono text-amber-600 dark:text-amber-400">{t.substitutions}</td>
                    <td className="text-center px-2 py-2 font-mono text-emerald-600 dark:text-emerald-400">{t.approved}</td>
                    <td className="text-center px-2 py-2 font-mono text-amber-600 dark:text-amber-400">{t.flagged}</td>
                    <td className="text-center px-2 py-2 font-mono text-red-600 dark:text-red-400">{t.rejected}</td>
                    <td className="text-center px-2 py-2 font-mono text-blue-600 dark:text-blue-400">{t.pending}</td>
                    <td className="text-right px-3 py-2">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${approvalRate >= 70 ? 'bg-emerald-500' : approvalRate >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${approvalRate}%` }} />
                        </div>
                        <span className={`font-mono text-[10px] w-8 text-right ${approvalRate >= 70 ? 'text-emerald-600 dark:text-emerald-400' : approvalRate >= 40 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                          {approvalRate}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visual Bars */}
      <div>
        <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mb-2">Approval Breakdown by Trade</p>
        <div className="space-y-2">
          {trades.map((t, i) => {
            const total = t.total_items || 1;
            return (
              <div key={i} className="bg-card border border-border rounded-sm p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-foreground">{t.trade}</span>
                  <span className="text-[10px] text-muted-foreground">{t.total_items} items</span>
                </div>
                <div className="flex h-3 rounded-sm overflow-hidden bg-muted">
                  {t.approved > 0 && (
                    <div className="bg-emerald-500" style={{ width: `${(t.approved / total) * 100}%` }}
                      title={`${t.approved} approved`} />
                  )}
                  {t.flagged > 0 && (
                    <div className="bg-amber-500" style={{ width: `${(t.flagged / total) * 100}%` }}
                      title={`${t.flagged} flagged`} />
                  )}
                  {t.rejected > 0 && (
                    <div className="bg-red-500" style={{ width: `${(t.rejected / total) * 100}%` }}
                      title={`${t.rejected} rejected`} />
                  )}
                  {t.pending > 0 && (
                    <div className="bg-blue-400" style={{ width: `${(t.pending / total) * 100}%` }}
                      title={`${t.pending} pending`} />
                  )}
                </div>
                <div className="flex gap-3 mt-1 text-[9px]">
                  {t.approved > 0 && <span className="text-emerald-500">{t.approved} approved</span>}
                  {t.flagged > 0 && <span className="text-amber-500">{t.flagged} flagged</span>}
                  {t.rejected > 0 && <span className="text-red-500">{t.rejected} rejected</span>}
                  {t.pending > 0 && <span className="text-blue-600 dark:text-blue-400">{t.pending} pending</span>}
                  {t.avg_turnaround_hours && (
                    <span className="text-muted-foreground ml-auto">
                      avg turnaround: {t.avg_turnaround_hours < 24
                        ? `${Math.round(t.avg_turnaround_hours)}h`
                        : `${Math.round(t.avg_turnaround_hours / 24)}d`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[9px] text-muted-foreground px-1">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Approved</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Flagged</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" /> Rejected</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-400" /> Pending</span>
      </div>
    </div>
  );
}
